import { useCallback, useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
    prompt(): Promise<void>;
}

/**
 * PWA install-prompt plumbing. Browsers fire `beforeinstallprompt` on
 * eligible pages; we squirrel the event away and surface a button that
 * calls `.prompt()` synchronously inside a click handler. Once
 * accepted (or dismissed) the event is one-shot, so we also listen for
 * `appinstalled` to remove the button.
 *
 * Returns `{ canInstall, install }` — `canInstall` is true when the
 * browser handed us a prompt and the app isn't already installed; the
 * `install` callback returns a boolean for whether the user accepted.
 */
export function useInstallPrompt(): { canInstall: boolean; install: () => Promise<boolean> } {
    const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
    const [installed, setInstalled] = useState<boolean>(() => {
        // If the page is already running standalone, treat as installed
        // and never show the button.
        if (typeof window === "undefined") return false;
        const mq = window.matchMedia?.("(display-mode: standalone)");
        return Boolean(mq?.matches);
    });

    useEffect(() => {
        const onBeforeInstall = (e: Event) => {
            e.preventDefault();
            setEvent(e as BeforeInstallPromptEvent);
        };
        const onInstalled = () => {
            setInstalled(true);
            setEvent(null);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const install = useCallback(async () => {
        if (!event) return false;
        try {
            await event.prompt();
            const choice = await event.userChoice;
            setEvent(null); // single-use prompt
            return choice.outcome === "accepted";
        } catch {
            return false;
        }
    }, [event]);

    return { canInstall: Boolean(event) && !installed, install };
}
